import type { CollectionEntry, CollectionIterateFn } from '../collection';
import { equals } from '../equals';
import { freezeCollection, hashCollection } from '../hash';
import {
    ImmutableMap,
    type ImmutableMapInit,
    type ImmutableMapKey,
    initializeNativeMap,
    MAP_TYPE_SYMBOL,
} from './map';

let emptyOrderedMap: undefined | ImmutableOrderedMap<any, any>;

/**
 * Immutable OrderedMap is an ordered collection of (key, value) pairs.
 */
export class ImmutableOrderedMap<Key extends ImmutableMapKey, Value> extends ImmutableMap<
    Key,
    Value
> {
    public static isKeyed = true;
    public static isOrdered = true;

    public static isOrderedMap(
        maybeMap: unknown
    ): maybeMap is ImmutableOrderedMap<ImmutableMapKey, unknown> {
        return (
            ImmutableMap.isMap(maybeMap) &&
            'isOrdered' in maybeMap.constructor &&
            typeof maybeMap.constructor.isOrdered === 'boolean' &&
            maybeMap.constructor.isOrdered
        );
    }

    public static create<K extends ImmutableMapKey, V>(
        values: ImmutableMapInit<K, V> = []
    ): ImmutableOrderedMap<K, V> {
        const map = initializeNativeMap(values);
        if (map.size === 0) {
            emptyOrderedMap =
                emptyOrderedMap || new ImmutableOrderedMap<K, V>(map, MAP_TYPE_SYMBOL);
            return emptyOrderedMap;
        }

        // @ts-ignore
        return freezeCollection(new ImmutableOrderedMap<K, V>(map, MAP_TYPE_SYMBOL));
    }

    public getByIndex(index: number): CollectionEntry<Key, Value> | undefined {
        // TODO: optimize by keeping a cache of index to key.
        const key = this.toKeysArray()[index];
        if (key === undefined) {
            return undefined;
        }
        if (!this._map.has(key)) {
            return undefined;
        }
        // @ts-ignore
        return [key, this._map.get(key)];
    }

    /**
     * Return the value at a given index.
     */
    public at(index: number): Value | undefined {
        let current = 0;
        for (const value of this._map.values()) {
            if (current === index) {
                return value;
            }
            current++;
        }
        return undefined;
    }

    /**
     * Returns a new OrderedMap representing a portion of this OrderedMap from start up to but not including end.
     */
    public slice(begin: number, end?: number): ImmutableOrderedMap<Key, Value> {
        return this.update(new Map(this.toArray().slice(begin, end)));
    }

    /**
     * Joins values together as a string, inserting a separator between each. The default separator is ",".
     */
    public join(separator = ','): string {
        return this.toValuesArray().join(separator);
    }

    /**
     * Super-set of the collection iterate.
     */
    public iterate(fn: CollectionIterateFn<Key, Value>): void {
        this._map.forEach((value, key) => fn(value, key));
    }

    /**
     * Compare the value of this Map to the value of another Map in an ordered fashion
     */
    public equals(other: any): boolean {
        if (this === other) {
            return true;
        }

        if (this.constructor !== other.constructor || this.size !== other.size) {
            return false;
        }

        if (hashCollection(this) !== hashCollection(other)) {
            return false;
        }

        const entries = this.toEntriesArray();
        const otherEntries = other.toEntriesArray();

        return entries.every(([_key, value], index) => {
            const otherValue = otherEntries[index][1];
            return value === otherValue || equals(value, otherValue);
        });
    }
}
